import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { HQ_PERMISSION_LEVELS } from 'src/constants';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { fetchAllPlatforms } from 'src/models/platform/platform';
import { isHQPlatform } from 'src/models/platform/utils';
import {
  fetchPlatformNodes,
  platformNodesQueryKey,
} from 'src/models/platform-node/platform-node';
import { isHeadTrainer } from 'src/models/user/utils';
import { Identifier } from 'src/types/utilityTypes';

import { getHqPermissionLevel } from './utils';
import {
  getHqAllowedLocationIds,
  isNodeDescendant,
  isPlatformDescendant,
} from './utils';

import type { PlatformTab } from 'src/models/platform/_types';
import type { PlatformNode } from 'src/models/platform-node/_types';
import type { Session } from 'src/models/session/_types';
import type { Trainer } from 'src/models/trainer/_types';
import type { SessionUser } from 'src/models/user/_types';

export interface HQPermissionHooks {
  canViewHQDashboard: () => boolean;
  canViewHQReports: () => boolean;
  canLoginToSite: (platformId: Identifier) => boolean;
  canManageHQSites: () => boolean;
  canCreateSiteNode: (parentNode?: PlatformNode) => boolean;
  canEditSiteNode: (node: PlatformNode) => boolean;
  canDeleteSiteNode: (node: PlatformNode) => boolean;
  canManageHQDataLoads: () => boolean;
  canSendHQDataLoads: () => boolean;
  canSendSpecificDataLoad: (loadTargets: string[]) => boolean;
  shouldRestrictToMyLocations: (area: string) => boolean;
  canAssignAllLevelPermissions: () => boolean;
  canManageHQStaff: () => boolean;
  canViewHQCorporateSettings: () => boolean;
  canViewHQTab: (tab: Partial<PlatformTab>) => boolean;
  canViewHQSidebarTab: (tab: PlatformTab) => boolean;
}

/**
 * Checks if a user can access a specific HQ area based on their permissions
 * @param area - The permission area to check (e.g., 'dashboards', 'reports')
 * @param session - Current session object
 * @param trainer - Current trainer object
 * @param sessionUser - Current session user
 * @returns boolean indicating if the user has access
 */
export const canViewHQArea = (
  area: string,
  session: Session | undefined,
  trainer: Trainer | undefined,
  sessionUser: SessionUser | null | undefined
): boolean => {
  // Only applies to HQ platforms
  if (!isHQPlatform(session)) return true;

  // If the area permission is present, honor it regardless of role
  const rawLevel = trainer?.hqPermissions?.[area]?.level as
    | number
    | string
    | undefined;
  if (rawLevel !== undefined) {
    return String(rawLevel) !== String(HQ_PERMISSION_LEVELS.MANAGE_NONE);
  }

  // Head trainers can always view any area
  if (sessionUser && isHeadTrainer(sessionUser)) return true;

  // Default to denying access for HQ platforms without explicit permissions
  return false;
};

export const useHQPermissionCheck = (): HQPermissionHooks => {
  const { session, trainer, user: sessionUser } = useOptionalSession();

  // Fetch platforms and nodes for permission checks
  const { data: platforms = [] } = useQuery({
    queryKey: ['platforms'],
    queryFn: async () => {
      const response = await fetchAllPlatforms();
      return response || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const { data: nodesData = { nodes: [], total: 0 } } = useQuery({
    queryKey: [platformNodesQueryKey],
    queryFn: () =>
      fetchPlatformNodes({
        includeHierarchyNodes: true,
        fetchAll: true,
      }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const nodes = nodesData.nodes;

  const canViewHQDashboard = useCallback(() => {
    return canViewHQArea('dashboards', session, trainer, sessionUser);
  }, [sessionUser, trainer, session]);

  const canViewHQReports = useCallback(() => {
    return canViewHQArea('reports', session, trainer, sessionUser);
  }, [sessionUser, trainer, session]);

  const canLoginToSite = useCallback(
    (platformId: Identifier) => {
      // Only applies to HQ platforms
      if (!isHQPlatform(session)) return false;

      // Head trainers can always login to any child platform
      if (sessionUser && isHeadTrainer(sessionUser)) return true;

      // Check explicit 'login_to_sites' permission
      const level = getHqPermissionLevel(trainer, 'loginToSites');
      if (level === HQ_PERMISSION_LEVELS.MANAGE_ALL) return true;
      if (level === HQ_PERMISSION_LEVELS.MANAGE_MY_LOCATIONS) {
        // For level 1, check if the platform matches the trainer's assignment
        if (trainer?.hqAssignmentType === 'platform') {
          return String(trainer.hqAssignmentId) === String(platformId);
        }
        // For platform_node assignments, use utility to check if platform is descendant
        if (trainer?.hqAssignmentType === 'platform_node') {
          const assignedNodeId = trainer.hqAssignmentId;
          if (!assignedNodeId || !platforms.length || !nodes.length) {
            return true; // Fall back to backend validation
          }

          // Use utility function to check if platform is descendant of assigned node
          return isPlatformDescendant(
            platformId,
            assignedNodeId,
            platforms,
            nodes
          );
        }
        return false;
      }
      return false;
    },
    [session, sessionUser, trainer, platforms, nodes]
  );

  const canManageHQSites = useCallback(() => {
    // Only applies to HQ platforms
    if (!isHQPlatform(session)) return false;

    // Head trainers can always manage sites
    if (sessionUser && isHeadTrainer(sessionUser)) return true;

    // Check if user has MANAGE_ALL level for sites (only level 0, not level 1)
    const level = getHqPermissionLevel(trainer, 'sites');
    return level === HQ_PERMISSION_LEVELS.MANAGE_ALL;
  }, [session, trainer, sessionUser]);

  const canCreateSiteNode = useCallback(
    (parentNode?: PlatformNode) => {
      // Only applies to HQ platforms
      if (!isHQPlatform(session)) return false;

      // Head trainers can always create nodes
      if (sessionUser && isHeadTrainer(sessionUser)) return true;

      const level = getHqPermissionLevel(trainer, 'sites');
      if (level === HQ_PERMISSION_LEVELS.MANAGE_ALL) return true;
      if (level === HQ_PERMISSION_LEVELS.MANAGE_MY_LOCATIONS) {
        // For level 1, check assignment type
        if (trainer?.hqAssignmentType === 'platform') {
          // Platform assignments cannot create nodes
          return false;
        }
        if (trainer?.hqAssignmentType === 'platform_node') {
          // Can create if parentNode is within accessible hierarchy
          if (!parentNode) return false; // Require parent for validation

          const assignedNodeId = trainer.hqAssignmentId;
          if (!assignedNodeId || !nodes.length) return true; // Fall back to backend validation

          // Use utility function to check if parentNode is a descendant of assigned node
          return isNodeDescendant(parentNode.id, assignedNodeId, nodes);
        }
      }
      return false;
    },
    [session, sessionUser, trainer, nodes]
  );

  const canEditSiteNode = useCallback(
    (node: PlatformNode) => {
      // Only applies to HQ platforms
      if (!isHQPlatform(session)) return false;

      // Head trainers can always edit nodes
      if (sessionUser && isHeadTrainer(sessionUser)) return true;

      const level = getHqPermissionLevel(trainer, 'sites');
      if (level === HQ_PERMISSION_LEVELS.MANAGE_ALL) return true;
      if (level === HQ_PERMISSION_LEVELS.MANAGE_MY_LOCATIONS) {
        if (trainer?.hqAssignmentType === 'platform') {
          // Platform assignments cannot edit nodes
          return false;
        }
        if (trainer?.hqAssignmentType === 'platform_node') {
          // Can edit if node is within accessible hierarchy
          const assignedNodeId = trainer.hqAssignmentId;
          if (!assignedNodeId || !nodes.length) return true; // Fall back to backend validation

          // Use utility function to check if target node is a descendant of assigned node
          return isNodeDescendant(node.id, assignedNodeId, nodes);
        }
      }
      return false;
    },
    [session, sessionUser, trainer, nodes]
  );

  const canDeleteSiteNode = useCallback(
    (node: PlatformNode) => {
      // Same logic as edit for now
      return canEditSiteNode(node);
    },
    [canEditSiteNode]
  );

  const canManageHQDataLoads = useCallback(() => {
    return canViewHQArea('dataLoad', session, trainer, sessionUser);
  }, [sessionUser, trainer, session]);

  const canSendHQDataLoads = useCallback(() => {
    return canViewHQArea('dataLoadSend', session, trainer, sessionUser);
  }, [sessionUser, trainer, session]);

  const canSendSpecificDataLoad = useCallback(
    (loadTargets: string[]) => {
      // Only applies to HQ platforms
      if (!isHQPlatform(session)) return true;

      // Head trainers can send any data load
      if (sessionUser && isHeadTrainer(sessionUser)) return true;

      const level = getHqPermissionLevel(trainer, 'dataLoadSend');

      // If no send permission at all, deny
      if (level === HQ_PERMISSION_LEVELS.MANAGE_NONE) return false;

      // If full send permission, allow
      if (level === HQ_PERMISSION_LEVELS.MANAGE_ALL) return true;

      // For MANAGE_MY_LOCATIONS level, check if data load targets match user's assignment
      if (level === HQ_PERMISSION_LEVELS.MANAGE_MY_LOCATIONS) {
        // If no targets specified, allow (targets all locations)
        if (!loadTargets || loadTargets.length === 0) return true;

        // Get user's allowed location IDs
        const allowedIds = getHqAllowedLocationIds(trainer, platforms, nodes);
        if (!allowedIds || allowedIds.length === 0) return false;

        // Check if all load targets are within user's allowed locations
        return loadTargets.every((target) => allowedIds.includes(target));
      }

      return false;
    },
    [session, sessionUser, trainer, platforms, nodes]
  );

  const shouldRestrictToMyLocations = useCallback(
    (area: string) => {
      // Only applies to HQ platforms
      if (!isHQPlatform(session)) return false;

      // Head trainers can see all locations
      if (sessionUser && isHeadTrainer(sessionUser)) return false;

      // Check if user has MANAGE_MY_LOCATIONS level for the specified area
      const level = getHqPermissionLevel(trainer, area);
      return level === HQ_PERMISSION_LEVELS.MANAGE_MY_LOCATIONS;
    },
    [session, sessionUser, trainer]
  );

  const canAssignAllLevelPermissions = useCallback(() => {
    // Only applies to HQ platforms
    if (!isHQPlatform(session)) return true;

    // Head trainers can always assign any permission level
    if (sessionUser && isHeadTrainer(sessionUser)) return true;

    // Check if user has MANAGE_ALL level for staff permissions
    // Users with MANAGE_MY_LOCATIONS (level 1) cannot assign MANAGE_ALL permissions
    const staffLevel = getHqPermissionLevel(trainer, 'staff');
    return staffLevel === HQ_PERMISSION_LEVELS.MANAGE_ALL;
  }, [session, sessionUser, trainer]);

  const canManageHQStaff = useCallback(() => {
    return canViewHQArea('staff', session, trainer, sessionUser);
  }, [sessionUser, trainer, session]);

  const canViewHQCorporateSettings = useCallback(() => {
    return canViewHQArea('corporateSettings', session, trainer, sessionUser);
  }, [sessionUser, trainer, session]);

  const canViewHQTab = useCallback(
    (tab: Partial<PlatformTab>) => {
      // Head trainers can always view all tabs
      if (sessionUser && isHeadTrainer(sessionUser)) {
        return true;
      }

      // Check HQ-specific permissions based on tab ID
      switch (tab.id) {
        case 'Dashboard':
          return canViewHQDashboard();
        case 'Reports':
          return canViewHQReports();
        case 'Data Loads':
          return canManageHQDataLoads();
        case 'Trainers': // Staff management
          return canManageHQStaff();
        case 'Site Configuration':
          return canViewHQCorporateSettings();
        default:
          // For other tabs not specifically covered by HQ permissions, allow access
          // These would be things like Messages, Clients, etc. that don't have HQ-specific restrictions
          return true;
      }
    },
    [
      sessionUser,
      canViewHQDashboard,
      canViewHQReports,
      canManageHQDataLoads,
      canManageHQStaff,
      canViewHQCorporateSettings,
    ]
  );

  const canViewHQSidebarTab = useCallback(() => {
    // Head trainers can always view all sidebar tabs
    if (sessionUser && isHeadTrainer(sessionUser)) {
      return true;
    }

    // For HQ platforms, most sidebar tabs are not restricted
    // Add specific checks here if needed in the future
    return true;
  }, [sessionUser]);

  return {
    canViewHQDashboard,
    canViewHQReports,
    canLoginToSite,
    canManageHQSites,
    canCreateSiteNode,
    canEditSiteNode,
    canDeleteSiteNode,
    canManageHQDataLoads,
    canSendHQDataLoads,
    canSendSpecificDataLoad,
    shouldRestrictToMyLocations,
    canAssignAllLevelPermissions,
    canManageHQStaff,
    canViewHQCorporateSettings,
    canViewHQTab,
    canViewHQSidebarTab,
  };
};
